import { DropzoneAreaField as BaseDropzoneAreaField } from '@wyz/ui';
import * as React from 'react';
import { useManageResources, useManageTenantResources } from '~/common/api';
import { useI18n } from '~/common/context';

type DropzoneAreaFieldProps = React.ComponentProps<
  typeof BaseDropzoneAreaField
>;

type Props = Omit<
  DropzoneAreaFieldProps,
  | 'translate'
  | 'isUploading'
  | 'createFunction'
  | 'resourceReader'
  | 'translations'
  | 'getDropRejectMessage'
> & { scope?: 'TENANT' | 'COMMON' };

function DropzoneAreaField({
  scope = 'COMMON',
  resourceType,
  maxFileSize,
  ...props
}: Props) {
  const { t } = useI18n();
  const manageResource = useManageResources();
  const manageTenantResources = useManageTenantResources();
  const SCOPE_PROPS = {
    TENANT: manageTenantResources,
    COMMON: manageResource,
  } as const;

  const getDropRejectMessage = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (rejectedFile: any) => {
      let message = t('app.common.file_input.file_rejected', {
        fileName: rejectedFile.file.name,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rejectedFile.errors?.forEach((error: any) => {
        if (error.code === 'file-invalid-type') {
          message += ' ' + t('app.common.file_input.file_type_not_supported');
        }
        if (error.code === 'file-too-large') {
          // Déterminer le message en fonction de la taille maximale
          const sizeInMB = maxFileSize ? maxFileSize / (1024 * 1024) : 0;
          if (sizeInMB === 1) {
            message += ' ' + t('app.common.assert.err_file_too_large_1mb');
          } else if (sizeInMB === 2) {
            message += ' ' + t('app.common.assert.err_file_too_large_2mb');
          } else {
            message +=
              ' ' +
              t('app.common.file_input.file_too_large', {
                maxSize: sizeInMB,
              });
          }
        }
      });

      return message;
    },
    [t, maxFileSize],
  );

  const getFileRemovedMessage = React.useCallback(
    (fileName: string) =>
      t('app.common.file_input.file_removed', {
        fileName,
      }),
    [t],
  );

  return (
    <BaseDropzoneAreaField
      {...props}
      resourceType={resourceType}
      maxFileSize={maxFileSize}
      translate={t}
      isUploading={SCOPE_PROPS[scope].mutation.isLoading}
      createFunction={SCOPE_PROPS[scope].handlers.uploadMany}
      resourceReader={SCOPE_PROPS[scope].handlers.getDownloadablePath}
      getDropRejectMessage={getDropRejectMessage}
      getFileRemovedMessage={getFileRemovedMessage}
      translations={{
        dropFilesMessage: t('app.common.file_input.drop_files_message'),
      }}
    />
  );
}

export default DropzoneAreaField;
